import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import moment from "moment";
import { preloadImagesToDataUrls } from "./pdfImagePreloader";

/**
 * Generates a PDF filename based on print type and note data
 */
export const getPdfFilename = (printType, note = {}, options = {}) => {
	const { isReferral = false, isElectronicFormOrder = false } = options;
	const PRESCRIPTION = "prescription";
	const NOTE = "note";
	const ASSESSMENT = "assessment";
	const EDUCATION_INFO = "education-info";

	if (printType === PRESCRIPTION || isReferral) {
		return `Rx_prescription-${moment().format("DD-MM-YYYY hh-mm-a").split(" ").join("_")}.pdf`;
	}

	if (isElectronicFormOrder || [EDUCATION_INFO, NOTE, ASSESSMENT].includes(printType)) {
		const suffix = printType === EDUCATION_INFO ? "-education-info" : printType === ASSESSMENT ? "-assessment" : "";
		return `Note-${note?.short_id || ""}${suffix}-${moment(
			`${note?.created_at?.date || note?.start_at?.date || ""} ${
				note?.created_at?.time || note?.start_at?.time || ""
			}`,
			"DD/MM/YYYY hh:mm a"
		)
			.format("DD-MM-YYYY hh-mm-a")
			.split(" ")
			.join("_")}.pdf`;
	}

	return "document.pdf";
};

/**
 * Extracts links from an HTML element with their positions
 */
const extractLinks = (element) => {
	const links = [];
	const linkElements = element.querySelectorAll("a[href]");
	const elementRect = element.getBoundingClientRect();

	linkElements.forEach((link) => {
		const rect = link.getBoundingClientRect();
		const href = link.getAttribute("href");

		if (href && (href.startsWith("http") || href.startsWith("mailto"))) {
			links.push({
				href,
				x: rect.left - elementRect.left,
				y: rect.top - elementRect.top,
				width: rect.width,
				height: rect.height,
			});
		}
	});

	return links;
};

/**
 * Generates a PDF from an HTML element using html2canvas + jsPDF
 */
export const generatePdfFromElement = async (element, options = {}) => {
	const {
		filename: _filename,
		printType: _printType,
		note: _note = {},
		isReferral: _isReferral = false,
		isElectronicFormOrder: _isElectronicFormOrder = false,
	} = options;

	// PDF configuration - A4 size
	const margin = 10; // mm
	const pdfWidth = 210; // A4 width in mm
	const pdfHeight = 297; // A4 height in mm
	const contentWidth = pdfWidth - margin * 2; // 190mm
	const contentHeight = pdfHeight - margin * 2; // 277mm

	// Convert contentWidth to pixels at 96 DPI for element constraint
	// 190mm * (96 / 25.4) ≈ 718px
	const targetWidthPx = Math.round((contentWidth * 96) / 25.4);

	// Store original styles
	const originalWidth = element.style.width;
	const originalMaxWidth = element.style.maxWidth;
	const originalBoxSizing = element.style.boxSizing;

	// Temporarily constrain element to target width for proper scaling
	element.style.boxSizing = "border-box";
	element.style.width = `${targetWidthPx}px`;
	element.style.maxWidth = `${targetWidthPx}px`;

	// Force reflow
	void element.offsetHeight;

	// Extract links AFTER setting width (so positions are accurate)
	const links = extractLinks(element);

	// Preload images (convert external URLs to base64, wait for lazy loads)
	await preloadImagesToDataUrls(element);

	// Capture the element with html2canvas
	const canvas = await html2canvas(element, {
		scale: 2, // Higher quality
		useCORS: true,
		logging: false,
		letterRendering: true,
		allowTaint: true,
		width: targetWidthPx,
	});

	// Restore original styles
	element.style.width = originalWidth || "";
	element.style.maxWidth = originalMaxWidth || "";
	element.style.boxSizing = originalBoxSizing || "";

	// Get canvas dimensions
	const canvasWidth = canvas.width;
	const canvasHeight = canvas.height;

	// Create PDF
	const doc = new jsPDF({
		orientation: "portrait",
		unit: "mm",
		format: "a4",
	});

	// Calculate image dimensions in mm
	const imgHeightMm = (canvasHeight / canvasWidth) * contentWidth;

	// Add the image, allowing it to span multiple pages
	let remainingHeight = imgHeightMm;
	let sourceY = 0;
	let currentPage = 0;

	while (remainingHeight > 0) {
		if (currentPage > 0) {
			doc.addPage();
		}

		// Calculate height for this page
		const pageContentHeight = Math.min(remainingHeight, contentHeight);

		// Calculate source dimensions from canvas
		const sourceHeight = (pageContentHeight / imgHeightMm) * canvasHeight;

		// Create a temporary canvas for this page's portion
		const pageCanvas = document.createElement("canvas");
		pageCanvas.width = canvasWidth;
		pageCanvas.height = sourceHeight;

		const ctx = pageCanvas.getContext("2d");
		ctx.drawImage(canvas, 0, sourceY, canvasWidth, sourceHeight, 0, 0, canvasWidth, sourceHeight);

		const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.95);

		// Add image to PDF
		doc.addImage(pageImgData, "JPEG", margin, margin, contentWidth, pageContentHeight);

		// Add link annotations for this page
		// Since we constrained the element to targetWidthPx, link coordinates are relative to that
		// Canvas is 2x the element size due to scale: 2, so original element width = canvasWidth / 2
		const elementWidth = canvasWidth / 2;
		const elementHeight = canvasHeight / 2;

		// Calculate which part of the original element is on this page
		const pageStartYPx = ((currentPage * contentHeight) / imgHeightMm) * elementHeight;
		const pageEndYPx = pageStartYPx + (contentHeight / imgHeightMm) * elementHeight;

		links.forEach((link) => {
			if (link.y >= pageStartYPx && link.y < pageEndYPx) {
				// Link is on this page - convert to PDF coordinates
				// Scale factor: contentWidth (mm) / elementWidth (px)
				const scaleFactor = contentWidth / elementWidth;

				const linkX = margin + link.x * scaleFactor;
				const linkY = margin + (link.y - pageStartYPx) * scaleFactor;
				const linkWidth = link.width * scaleFactor;
				const linkHeight = link.height * scaleFactor;

				if (linkX >= 0 && linkX <= pdfWidth && linkY >= 0 && linkY <= pdfHeight) {
					doc.link(linkX, linkY, linkWidth, linkHeight, { url: link.href });
				}
			}
		});

		sourceY += sourceHeight;
		remainingHeight -= pageContentHeight;
		currentPage++;
	}

	return doc.output("blob");
};
